import React, { useEffect, useRef, useState } from "react";
import apiClient from "../../api/index";
import { changeMemberName, fetchMyPageData } from "../../api/members";
import SearchBar from "../common/components/SearchBar";
import { showToast } from "../common/components/Toast";

// 회원이 2,000명 이상이라 검색 전에는 상위 일부만 렌더링한다 (성능)
const VISIBLE_MEMBERS_LIMIT = 50;

const convertToKST = (utcDate) => {
  if (!utcDate) return "시간 정보 없음";
  return new Date(utcDate).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" });
};

const MemberInfo = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedAccountId, setExpandedAccountId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // 다른 행을 빠르게 클릭할 때 늦게 도착한 이전 응답이 화면을 덮지 않도록 현재 펼친 행을 추적
  const expandedAccountIdRef = useRef(null);
  const [editingAccountId, setEditingAccountId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    const loadMembers = async () => {
      setLoading(true);
      setError(null);
      try {
        // 포인트 잔액이 낡지 않도록 캐시(fetchMembers) 대신 항상 직접 조회
        const response = await apiClient.get("/members");
        setMembers(response.data);
      } catch (err) {
        console.error("❌ 회원 목록 조회 실패:", err.response?.data || err.message);
        setError("회원 목록을 불러오는 중 오류가 발생했습니다.");
      } finally {
        setLoading(false);
      }
    };

    loadMembers();
  }, []);

  const trimmedSearchTerm = searchTerm.trim();
  const filteredMembers = trimmedSearchTerm
    ? members.filter(
        (member) =>
          (member.name || "").includes(trimmedSearchTerm) ||
          (member.phone_number || "").includes(trimmedSearchTerm)
      )
    : members.slice(0, VISIBLE_MEMBERS_LIMIT);

  const closeDetail = () => {
    expandedAccountIdRef.current = null;
    setExpandedAccountId(null);
    setDetail(null);
    setDetailLoading(false);
  };

  const openDetail = async (member) => {
    expandedAccountIdRef.current = member.account_id;
    setExpandedAccountId(member.account_id);
    setDetail(null);
    setDetailLoading(true);

    // 인자는 account_id다 — member.id(members.id)와 혼동 금지
    const data = await fetchMyPageData(member.account_id);
    if (expandedAccountIdRef.current !== member.account_id) return;

    setDetail(data);
    setDetailLoading(false);
  };

  const startEditName = (member) => {
    setEditingAccountId(member.account_id);
    setEditingName(member.name || "");
  };

  const cancelEditName = () => {
    setEditingAccountId(null);
    setEditingName("");
  };

  const saveName = async (member) => {
    const newName = editingName.trim();
    if (!newName) {
      showToast("이름을 입력해 주세요.", "fail");
      return;
    }
    if (newName === member.name) {
      cancelEditName();
      return;
    }

    setSavingName(true);
    try {
      const result = await changeMemberName(member.account_id, newName);
      // 목록을 다시 불러오지 않고 해당 행만 갱신한다 (검색 상태 유지)
      setMembers((prev) =>
        prev.map((m) => (m.account_id === member.account_id ? { ...m, name: result.name } : m))
      );
      showToast(result.message, "success");
      cancelEditName();
    } catch (err) {
      showToast(err.response?.data?.message || "이름 변경에 실패했습니다.", "fail");
    } finally {
      setSavingName(false);
    }
  };

  const handleToggleDetail = (member) => {
    if (expandedAccountId === member.account_id) {
      closeDetail();
    } else {
      openDetail(member);
    }
  };

  const renderDetail = () => {
    if (detailLoading) return <p>로딩 중...</p>;
    if (!detail) return <p className="text-red-500">구매 이력을 불러오는 중 오류가 발생했습니다.</p>;
    if (!detail.transactions || detail.transactions.length === 0) {
      return <p className="text-gray-500">구매 이력이 없습니다.</p>;
    }

    return (
      <div className="flex flex-col gap-3">
        {detail.transactions.map((txn) => (
          <div key={txn.id} className="p-4 bg-white rounded-lg shadow">
            <p className="text-gray-500 text-sm">{convertToKST(txn.created_at)}</p>
            <ul className="mt-2">
              {txn.items.map((item) => (
                <li key={item.product_id} className="flex justify-between">
                  <span>{item.name} x {item.quantity}</span>
                  <span>{((item.price ?? 0) * (item.quantity ?? 1)).toLocaleString()}원</span>
                </li>
              ))}
            </ul>
            <div className="mt-3">
              <p>결제 금액: <span className="font-bold">{Math.floor(txn.final_amount ?? 0).toLocaleString()}원</span></p>
              <p>결제 수단: <span className="text-gray-600">{txn.payment_method || "정보 없음"}</span></p>
              <p>사용 포인트: <span className="text-red-500">{Math.floor(txn.discount ?? 0).toLocaleString()}p</span></p>
              <p>적립 포인트: <span className="text-green-500">{Math.floor(txn.earned_points ?? 0).toLocaleString()}p</span></p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="font-600 text-2xl">회원 정보</div>
      {loading ? (
        <p>로딩 중...</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : (
        <div className="mt-4">
          <SearchBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            placeholder="이름 또는 전화번호로 검색해 보세요"
          />
          {!trimmedSearchTerm && (
            <p className="text-gray-500 text-sm mt-2">
              전체 {members.length.toLocaleString()}명 중 상위 {VISIBLE_MEMBERS_LIMIT}명만 표시합니다. 검색으로 찾아주세요.
            </p>
          )}

          <table className="w-full border-collapse border border-gray-300 mt-4">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-2">이름</th>
                <th className="border p-2">전화번호</th>
                <th className="border p-2">포인트</th>
                <th className="border p-2">관리</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center p-4 text-gray-400">검색 결과가 없습니다.</td>
                </tr>
              ) : (
                filteredMembers.map((member) => (
                  <React.Fragment key={member.id}>
                    <tr className="hover:bg-gray-100">
                      <td className="border p-2">
                        {editingAccountId === member.account_id ? (
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveName(member);
                              if (e.key === "Escape") cancelEditName();
                            }}
                            maxLength={50}
                            autoFocus
                            className="w-full px-2 py-1 border border-gray-900 rounded-md"
                          />
                        ) : (
                          member.name
                        )}
                      </td>
                      <td className="border p-2">{member.phone_number}</td>
                      <td className="border p-2 text-right">{(member.points ?? 0).toLocaleString()}p</td>
                      <td className="border p-2 text-center whitespace-nowrap">
                        {editingAccountId === member.account_id ? (
                          <>
                            <button
                              className="bg-gray-900 text-white text-sm font-500 px-2.5 py-1 rounded-md disabled:bg-gray-400"
                              onClick={() => saveName(member)}
                              disabled={savingName}
                            >
                              {savingName ? "저장 중..." : "저장"}
                            </button>
                            <button
                              className="ml-1 bg-gray-100 text-gray-900 text-sm font-500 px-2.5 py-1 rounded-md"
                              onClick={cancelEditName}
                              disabled={savingName}
                            >
                              취소
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="bg-gray-100 text-gray-900 text-sm font-500 px-2.5 py-1 rounded-md"
                              onClick={() => startEditName(member)}
                            >
                              이름수정
                            </button>
                            <button
                              className="ml-1 bg-gray-900 text-white text-sm font-500 px-2.5 py-1 rounded-md"
                              onClick={() => handleToggleDetail(member)}
                            >
                              {expandedAccountId === member.account_id ? "닫기" : "상세보기"}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                    {expandedAccountId === member.account_id && (
                      <tr>
                        <td colSpan="4" className="border p-4 bg-gray-50">{renderDetail()}</td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MemberInfo;
